/**
 * Email verification landing page.
 *
 * The verification email links to `${APP_URL}/auth/verify-email?token=...`
 * (frontend URL — no /api prefix). This page picks up the token from the
 * query string and forwards it to the existing backend endpoint
 * `GET /api/auth/verify-email?token=...` which:
 *   - marks the user as `email_verified=true`
 *   - for Lifetime users, flips their license to `status=active, enabled=true`
 *
 * Reuses the existing backend verify endpoint, login route, and brand styling.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Loader2, MailCheck, AlertTriangle } from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [state, setState] = useState('loading'); // loading | success | error
  const [error, setError] = useState('');
  const calledRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setState('error');
      setError('Missing verification token. Please use the link from your email.');
      return;
    }
    // Idempotency:
    //  • React StrictMode (dev) double-invokes effects, which would otherwise
    //    burn the token and show "already used" on the 2nd run.
    //  • If a user reloads after a successful verification, show success
    //    instead of an error from the consumed-token state.
    const sessionKey = `lt_verified:${token}`;
    if (sessionStorage.getItem(sessionKey) === '1') {
      setState('success');
      return;
    }
    if (calledRef.current) return;
    calledRef.current = true;
    axios
      .get(`${API_BASE}/auth/verify-email`, { params: { token } })
      .then(() => {
        sessionStorage.setItem(sessionKey, '1');
        setState('success');
      })
      .catch((err) => {
        setState('error');
        setError(err?.response?.data?.detail || 'Verification failed. The link may have expired.');
      });
  }, [token]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10 bg-gradient-to-br from-slate-50 to-orange-50/40"
      data-testid="verify-email-page"
    >
      <div className="w-full max-w-md">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-5">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">RS</span>
            </div>
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Manrope' }}>
              ReStaff
            </span>
          </div>

          {state === 'loading' && (
            <div data-testid="verify-loading">
              <Loader2 size={32} className="mx-auto text-orange-500 animate-spin" />
              <p className="mt-4 text-sm text-slate-600">Verifying your email…</p>
            </div>
          )}

          {state === 'success' && (
            <div data-testid="verify-success">
              <div className="w-12 h-12 rounded-xl bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4">
                <MailCheck size={24} />
              </div>
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                Email verified
              </h1>
              <p className="text-sm text-slate-600 mt-2">
                Your account is active. You can now sign in.
              </p>
              <Button
                type="button"
                className="w-full mt-5 bg-orange-500 hover:bg-orange-600 text-white"
                onClick={() => navigate('/login')}
                data-testid="verify-success-login"
              >
                Continue to sign in
              </Button>
            </div>
          )}

          {state === 'error' && (
            <div data-testid="verify-error">
              <div className="w-12 h-12 rounded-xl bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={24} />
              </div>
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                Verification failed
              </h1>
              <p className="text-sm text-slate-600 mt-2" data-testid="verify-error-msg">{error}</p>
              <p className="text-xs text-slate-500 mt-3">
                Lifetime customers can request a new link from the redemption page.
              </p>
              <div className="flex flex-col gap-2 mt-5">
                <Link to="/lifetime">
                  <Button type="button" variant="outline" className="w-full" data-testid="verify-error-resend-link">
                    Open redemption page
                  </Button>
                </Link>
                <Link to="/login">
                  <Button type="button" variant="ghost" className="w-full" data-testid="verify-error-login-link">
                    Back to sign in
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
